import { Bot, ChevronDown, ChevronUp, CircleAlert, Wrench } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { AgentRunEvent, AgentRunTranscript as TranscriptPage } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import CenteredSpinner from "../CenteredSpinner.tsx";

function ToolArgs({ payload }: { payload: Record<string, unknown> | null }) {
  const [open, setOpen] = useState(false);
  if (!payload || Object.keys(payload).length === 0) return null;
  return (
    <>
      <Typography
        component="button"
        variant="caption"
        onClick={() => setOpen(!open)}
        sx={{
          color: "text.secondary",
          background: "none",
          border: "none",
          p: 0,
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {open ? "hide arguments" : "show arguments"}
      </Typography>
      <Collapse in={open}>
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.5,
            p: 1,
            fontSize: 12,
            bgcolor: "action.hover",
            borderRadius: 1,
            overflow: "auto",
            maxHeight: 200,
          }}
        >
          {JSON.stringify(payload, null, 2)}
        </Box>
      </Collapse>
    </>
  );
}

function EventRow({ event }: { event: AgentRunEvent }) {
  const [showResult, setShowResult] = useState(false);

  if (event.kind === "thinking") {
    return (
      <Box sx={{ px: 2, py: 0.75 }}>
        <Typography
          variant="body2"
          sx={{ color: "text.disabled", fontStyle: "italic", whiteSpace: "pre-wrap" }}
        >
          {event.text}
        </Typography>
      </Box>
    );
  }

  if (event.kind === "tool_use") {
    return (
      <Box sx={{ px: 2, py: 0.75, display: "flex", gap: 1, alignItems: "flex-start" }}>
        <Wrench fontSize="small" style={{ marginTop: 2, opacity: 0.6 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {event.tool_name}
          </Typography>
          <ToolArgs payload={event.payload} />
        </Box>
      </Box>
    );
  }

  if (event.kind === "tool_result") {
    return (
      <Box sx={{ px: 2, py: 0.5, pl: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: event.is_error ? "error.main" : "text.secondary" }}
          >
            {event.is_error ? "tool error" : "result"}
          </Typography>
          <IconButton size="small" onClick={() => setShowResult(!showResult)}>
            {showResult ? <ChevronUp fontSize="small" /> : <ChevronDown fontSize="small" />}
          </IconButton>
        </Box>
        <Collapse in={showResult}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              fontSize: 12,
              bgcolor: "action.hover",
              borderRadius: 1,
              overflow: "auto",
              maxHeight: 240,
              whiteSpace: "pre-wrap",
            }}
          >
            {event.text || "(empty)"}
          </Box>
        </Collapse>
      </Box>
    );
  }

  if (event.kind === "error") {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Alert severity="error" icon={<CircleAlert />}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {event.text}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // assistant_text and result both read as the agent talking.
  return (
    <Box sx={{ px: 2, py: 1, display: "flex", gap: 1, alignItems: "flex-start" }}>
      <Bot fontSize="small" style={{ marginTop: 3, opacity: 0.7 }} />
      <Box sx={{ minWidth: 0 }}>
        {event.kind === "result" && (
          <Chip
            size="small"
            label={event.is_error ? "ended with an error" : "finished"}
            color={event.is_error ? "error" : "success"}
            sx={{ mb: 0.5 }}
          />
        )}
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {event.text}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Live transcript of one agent run, for any agent. The owner of the run (a
 * prospect order, a signal) supplies `fetchPage`, which hits its own
 * access-checked transcript endpoint.
 */
export default function AgentRunTranscript({
  runId,
  initiallyActive,
  fetchPage,
}: {
  runId: string;
  initiallyActive: boolean;
  fetchPage: (afterSeq: number) => Promise<TranscriptPage>;
}) {
  // Accumulated across fetches (one per pushed transcript line): each request
  // returns only events after `cursor`.
  const [events, setEvents] = useState<AgentRunEvent[]>([]);
  const cursor = useRef(0);
  const [isActive, setIsActive] = useState(initiallyActive);
  const bottom = useRef<HTMLDivElement | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: keys.agentRun.transcript(runId),
    // The cursor lives in a ref so advancing it does not re-key the query and
    // throw away what has already been accumulated.
    queryFn: () => fetchPage(cursor.current),
    meta: { live: true },
  });

  useEffect(() => {
    if (!data) return;
    if (data.events.length > 0) {
      cursor.current = data.last_seq;
      setEvents((prev) => {
        // Guard against a duplicate page (a refetch racing a poll).
        const seen = new Set(prev.map((e) => e.seq));
        return [...prev, ...data.events.filter((e) => !seen.has(e.seq))];
      });
    }
    setIsActive(data.is_active);
  }, [data]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "nearest" });
  }, [events.length]);

  if (isLoading && events.length === 0) {
    return <CenteredSpinner py={2} size={20} />;
  }

  if (events.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary", px: 2, py: 1 }}>
        {isActive
          ? "Waiting for the agent's first message…"
          : "This run produced no transcript."}
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mt: 1 }}>
      <Box sx={{ maxHeight: 420, overflow: "auto", py: 1 }}>
        <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
          {events.map((event) => (
            <EventRow key={event.seq} event={event} />
          ))}
        </Stack>
        <div ref={bottom} />
      </Box>
      {isActive && (
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <CircularProgress size={12} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Agent is working…
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
